import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Copy, Check, Play, Eye, UserCog, RotateCcw, LogOut, Users, Send } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import Avatar from '../../components/Avatar'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import { useUserDirectory } from '../../hooks/useUserDirectory'
import { useFriends } from '../../hooks/useFriends'
import { useSpyOnline } from '../../hooks/useSpyOnline'
import { useLogSpyResult } from '../../components/games/spy/hooks'
import { TIMER_OPTIONS, MIN_PLAYERS } from '../../components/games/spy/logic'
import { cn } from '../../lib/utils'

export function SpyOnline() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const { user } = useAuth()
  const o = useSpyOnline()
  const logResult = useLogSpyResult()

  const locName = (loc) => (locale === 'ar' ? loc?.name_ar : loc?.name_en) || loc?.name_en || ''
  const roleText = (loc, idx) => (idx == null ? '' : ((locale === 'ar' ? loc?.roles_ar : loc?.roles_en)?.[idx] || ''))

  const { resolve } = useUserDirectory()
  const [name, setName] = useState('')
  // Prefill my lobby name from the profile display name once the
  // directory resolves — but never stomp something already typed.
  const myName = resolve(user?.id, locale).name
  useEffect(() => {
    if (myName) setName((prev) => prev || myName)
  }, [myName])
  // ?join=CODE deep link (spy-invite push) prefills the join field.
  const [code, setCode] = useState(() => {
    try {
      const c = new URLSearchParams(window.location.search).get('join') || ''
      return c.toUpperCase().slice(0, 6)
    } catch { return '' }
  })
  const [timer, setTimer] = useState(8)
  const [copied, setCopied] = useState(false)

  // ── entry: create / join ──────────────────────────────────────
  if (!o.gameId || !o.game || o.game.status === 'abandoned') {
    return (
      <div className="space-y-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('games.spy.yourName')}
          className="w-full h-11 px-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none"
          dir="auto"
        />
        <Card>
          <CardContent className="p-4 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.createRoom')}</p>
            <div className="grid grid-cols-3 gap-2">
              {TIMER_OPTIONS.map((m) => (
                <button key={m} type="button" onClick={() => setTimer(m)}
                  className={cn('h-9 rounded-lg text-sm font-semibold', timer === m ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground')} dir="auto">
                  {t('games.spy.minutes', { n: m })}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => o.create(name, timer)} disabled={o.busy || !name.trim()}
              className="w-full h-11 rounded-lg bg-app-primary-500 text-white font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2" dir="auto">
              {o.busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Users className="w-5 h-5" />} {t('games.spy.createRoom')}
            </button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.joinRoom')}</p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t('games.spy.codePlaceholder')}
              maxLength={6}
              className="w-full h-11 px-3 rounded-lg border border-input bg-background text-foreground text-base tracking-[0.3em] text-center font-mono focus:outline-none"
              dir="ltr"
            />
            <button type="button" onClick={() => o.join(code, name)} disabled={o.busy || !name.trim() || code.length < 6}
              className="w-full h-11 rounded-lg bg-muted text-foreground font-bold disabled:opacity-50" dir="auto">
              {t('games.spy.joinRoom')}
            </button>
          </CardContent>
        </Card>
        {o.error === 'not_found' && <p className="text-[12px] text-red-500 text-center" dir="auto">{t('games.spy.roomNotFound')}</p>}
        {o.error === 'in_progress' && <p className="text-[12px] text-red-500 text-center" dir="auto">{t('games.spy.roomInProgress')}</p>}
      </div>
    )
  }

  // ── lobby ──────────────────────────────────────────────────────
  if (o.game.status === 'lobby') {
    const copy = async () => { try { await navigator.clipboard.writeText(o.game.code); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* ignore */ } }
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-5 text-center space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.roomCode')}</p>
            <button type="button" onClick={copy} className="inline-flex items-center gap-2 text-3xl font-bold tracking-[0.3em] font-mono text-app-primary-500" dir="ltr">
              {o.game.code} {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-muted-foreground" />}
            </button>
            <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.shareCode')}</p>
          </CardContent>
        </Card>
        <InviteFriendsCard code={o.game.code} t={t} />
        <Card>
          <CardContent className="p-0">
            {o.players.map((p) => (
              <div key={p.user_id} className="px-4 py-3 flex items-center gap-2 border-b border-border/60" dir="auto">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-foreground">{p.name}</span>
                {p.user_id === o.game.host_id && <span className="text-[10px] text-app-primary-500 ms-auto">{t('games.spy.host')}</span>}
              </div>
            ))}
            {o.bots.map((b) => (
              <div key={b.id} className="px-4 py-3 flex items-center gap-2 border-b border-border/60 last:border-b-0 text-muted-foreground" dir="auto">
                <span className="text-sm">{b.name}</span>
                <span className="text-[10px] ms-auto">{t('games.spy.computerPlayers')}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        {o.isHost && (
          <div className="flex items-center justify-between gap-2 px-1">
            <span className="text-sm text-foreground" dir="auto">{t('games.spy.computerPlayers')}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={o.removeBot} disabled={o.bots.length <= 0} className="w-8 h-8 rounded-full bg-muted text-foreground disabled:opacity-40 inline-flex items-center justify-center">−</button>
              <span className="w-5 text-center text-sm font-semibold tabular-nums">{o.bots.length}</span>
              <button type="button" onClick={o.addBot} disabled={o.players.length + o.bots.length >= 12} className="w-8 h-8 rounded-full bg-muted text-foreground disabled:opacity-40 inline-flex items-center justify-center">+</button>
            </div>
          </div>
        )}
        {o.isHost ? (
          <button type="button" onClick={o.start} disabled={o.busy || (o.players.length + o.bots.length) < MIN_PLAYERS}
            className="w-full py-3.5 rounded-xl bg-app-primary-500 text-white font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2" dir="auto">
            {o.busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 rtl:-scale-x-100" />} {t('games.spy.start')}
          </button>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2" dir="auto">{t('games.spy.waitingHost')}</p>
        )}
        {(o.players.length + o.bots.length) < MIN_PLAYERS && <p className="text-[12px] text-muted-foreground text-center" dir="auto">{t('games.spy.needMore', { n: MIN_PLAYERS })}</p>}
        <LeaveButton onLeave={o.leave} t={t} />
      </div>
    )
  }

  // ── playing ────────────────────────────────────────────────────
  if (o.game.status === 'playing') {
    return (
      <PlayingView
        game={o.game} myRole={o.myRole} location={o.location} seats={o.seats}
        bots={o.bots} events={o.events} botTurn={o.botTurn}
        userId={user?.id} isHost={o.isHost}
        onVote={o.vote} onReveal={o.reveal} onLeave={o.leave}
        locName={locName} roleText={roleText} t={t}
      />
    )
  }

  // ── revealed ───────────────────────────────────────────────────
  return (
    <RevealedView
      o={o} logResult={logResult} locName={locName} t={t}
    />
  )
}

function PlayingView({ game, myRole, location, seats, bots, events, botTurn, userId, isHost, onVote, onReveal, onLeave, locName, roleText, t }) {
  const [now, setNow] = useState(Date.now())
  const [botBusy, setBotBusy] = useState(false)
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id) }, [])
  const endsAt = useMemo(() => (game.started_at ? new Date(game.started_at).getTime() + game.timer_minutes * 60000 : 0), [game.started_at, game.timer_minutes])
  const left = Math.max(0, Math.floor((endsAt - now) / 1000))
  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`
  const isSpy = myRole?.is_spy
  const myVote = myRole?.voted_for

  const doBotTurn = async () => { setBotBusy(true); await botTurn('ask'); setBotBusy(false) }

  return (
    <div className="space-y-4">
      {/* My secret card */}
      <Card>
        <CardContent className={cn('p-5 text-center space-y-2', isSpy && 'bg-red-500/10')}>
          {isSpy ? (
            <>
              <div className="text-4xl">🕵️</div>
              <p className="text-xl font-bold text-red-500" dir="auto">{t('games.spy.youAreSpy')}</p>
              <p className="text-sm text-muted-foreground" dir="auto">{t('games.spy.spyHint')}</p>
            </>
          ) : (
            <>
              <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.theLocation')}</p>
              <p className="text-2xl font-bold text-foreground" dir="auto">{locName(location)}</p>
              {roleText(location, myRole?.role_index) && (
                <p className="inline-flex items-center gap-1.5 text-sm text-app-primary-500" dir="auto">
                  <UserCog className="w-4 h-4" /> {t('games.spy.yourRole', { role: roleText(location, myRole?.role_index) })}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <div className="text-center text-4xl font-bold tabular-nums text-foreground" dir="ltr">{mmss}</div>

      {/* Bot feed — the computer asks, everyone answers out loud. */}
      {bots.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <button type="button" onClick={doBotTurn} disabled={botBusy}
              className="w-full h-10 rounded-lg bg-app-primary-500/15 text-app-primary-500 text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2" dir="auto">
              {botBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>🤖</span>} {t('games.spy.computerTurn')}
            </button>
            {events.length > 0 && (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {events.map((e) => (
                  <div key={e.id} className="text-sm rounded-lg bg-muted/60 px-2.5 py-1.5" dir="auto">
                    <span className="font-semibold text-app-primary-500">{e.actor_name}: </span>
                    <span className="text-foreground">{e.text}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vote */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.voteWho')}</p>
          <div className="grid grid-cols-2 gap-2">
            {seats.filter((s) => s.id !== userId).map((s) => (
              <button key={s.id} type="button" onClick={() => onVote(s.id)}
                className={cn('h-10 rounded-lg text-sm font-semibold truncate px-2', myVote === s.id ? 'bg-app-primary-500 text-white' : 'bg-muted text-foreground')} dir="auto">
                {s.name}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {isHost && (
        <button type="button" onClick={onReveal} className="w-full py-3 rounded-xl bg-app-primary-500 text-white font-bold inline-flex items-center justify-center gap-2" dir="auto">
          <Eye className="w-5 h-5" /> {t('games.spy.reveal')}
        </button>
      )}
      <LeaveButton onLeave={onLeave} t={t} />
    </div>
  )
}

function RevealedView({ o, logResult, locName, t }) {
  const loggedRef = useRef(false)
  const spyNames = o.spyIds.map((id) => o.nameOf(id))
  const topVote = o.tally[0]
  const caught = topVote && o.spyIds.includes(topVote.userId)

  // Host logs the round once to the shared scoreboard. No deps array
  // on purpose — the ref guard makes it fire exactly once, after the
  // reveal data (location/spies) has loaded.
  useEffect(() => {
    if (loggedRef.current || !o.isHost || !o.location) return
    loggedRef.current = true
    logResult({
      locationId: o.location?.id,
      locationName: locName(o.location),
      players: o.players.map((p) => p.name),
      spyNames,
      outcome: caught ? 'spy_caught' : 'spy_escaped',
    })
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5 text-center space-y-3">
          <div className="text-4xl">🕵️</div>
          <div>
            <p className="text-[11px] text-muted-foreground" dir="auto">{spyNames.length > 1 ? t('games.spy.theSpies') : t('games.spy.theSpy')}</p>
            <p className="text-xl font-bold text-app-primary-500" dir="auto">{spyNames.join('، ')}</p>
          </div>
          <div className="border-t border-border/60 pt-3">
            <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.theLocation')}</p>
            <p className="text-lg font-bold text-foreground" dir="auto">{locName(o.location)}</p>
          </div>
          <div className={cn('text-sm font-semibold', caught ? 'text-emerald-500' : 'text-red-500')} dir="auto">
            {caught ? t('games.spy.outcomeCaught') : t('games.spy.outcomeEscaped')}
          </div>
        </CardContent>
      </Card>

      {o.tally.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.votes')}</p>
            {o.tally.map((v) => (
              <div key={v.userId} className="flex items-center justify-between text-sm" dir="auto">
                <span className="text-foreground">{v.name}</span>
                <span className="text-muted-foreground tabular-nums">{v.votes}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {o.isHost && (
        <button type="button" onClick={o.start} className="w-full py-3 rounded-xl bg-app-primary-500 text-white font-bold inline-flex items-center justify-center gap-2" dir="auto">
          <RotateCcw className="w-5 h-5" /> {t('games.spy.playAgain')}
        </button>
      )}
      <LeaveButton onLeave={o.leave} t={t} />
    </div>
  )
}

// Lobby invite list — one tap pushes a friend the room code with a
// /games/spy?join=CODE deep link (friends-push kind 'spy-invite';
// friendship + room membership are verified server-side).
function InviteFriendsCard({ code, t }) {
  const { friends } = useFriends()
  const [state, setState] = useState({}) // userId -> 'busy' | 'sent'

  if (!friends.length) return null

  const invite = async (f) => {
    if (state[f.other_user_id]) return
    setState((s) => ({ ...s, [f.other_user_id]: 'busy' }))
    const { error } = await supabase.functions
      .invoke('friends-push', { body: { kind: 'spy-invite', to: f.other_user_id, code } })
      .catch(() => ({ error: true }))
    setState((s) => ({ ...s, [f.other_user_id]: error ? undefined : 'sent' }))
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">
          {t('games.spy.inviteFriends')}
        </p>
        <div className="divide-y divide-border/60">
          {friends.map((f) => {
            const name = f.display_name || (f.username ? `@${f.username}` : '')
            const s = state[f.other_user_id]
            return (
              <div key={f.other_user_id} className="py-2 flex items-center gap-2.5">
                <Avatar userId={f.other_user_id} profile={f} size={28} className="flex-shrink-0" />
                <span className="flex-1 min-w-0 text-sm text-foreground truncate" dir="auto">{name}</span>
                {s === 'sent' ? (
                  <span className="text-xs text-emerald-500 flex-shrink-0" dir="auto">{t('games.spy.invited')}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => invite(f)}
                    disabled={s === 'busy'}
                    className="px-2.5 py-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                    dir="auto"
                  >
                    {s === 'busy'
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Send className="w-3.5 h-3.5 rtl:-scale-x-100" />}
                    {t('games.spy.invite')}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function LeaveButton({ onLeave, t }) {
  return (
    <button type="button" onClick={onLeave} className="w-full py-2.5 rounded-xl border-2 border-border text-muted-foreground text-sm font-semibold inline-flex items-center justify-center gap-2" dir="auto">
      <LogOut className="w-4 h-4 rtl:-scale-x-100" /> {t('games.spy.leaveRoom')}
    </button>
  )
}

export default SpyOnline
